import type {
  CreateTravelBodyTravel,
  CreateTravelBodyTravelConditionsInnerValue,
  CreateTravelBodyTravelJourneyStayingConditionsEnum,
  Travel,
} from "@syadem/kairos-citizen-js";
import type { StayingCondition, TravelLeg, TravelRecord, TravelSnapshot } from "../../domain/travel";

/** The generated client parses `YYYY-MM-DD` as UTC midnight and serializes it back with `toISOString()`. */
function toApiDate(date: string): Date {
  return new Date(date);
}

function fromApiDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function fromApiStayingCondition(stayingCondition: string | null | undefined): StayingCondition | null {
  return stayingCondition === "comfortable" || stayingCondition === "intermediate" || stayingCondition === "adventurous"
    ? stayingCondition
    : null;
}

function toApiLeg({ countrycode, start_date, end_date, staying_conditions }: TravelLeg) {
  if (countrycode === null || start_date === null || end_date === null || staying_conditions === null) {
    throw new Error("Cannot save a travel with an incomplete leg");
  }

  return {
    countryCode: countrycode,
    startDate: toApiDate(start_date),
    endDate: toApiDate(end_date),
    stayingConditions: staying_conditions,
  };
}

export function toApiTravel(travel: TravelSnapshot): CreateTravelBodyTravel {
  return {
    journey: {
      legs: travel.journey.legs.map(toApiLeg),
      // The API accepts a null journey staying condition, which the generated type does not express.
      stayingConditions: travel.journey.staying_conditions as CreateTravelBodyTravelJourneyStayingConditionsEnum,
    },
    // Condition values are passed through as-is by the generated client (YYYY-MM-DD date, number, boolean or null).
    conditions: travel.conditions.map(({ id, value }) => ({
      id,
      value: value as CreateTravelBodyTravelConditionsInnerValue,
    })),
    viewedQuestionnaireSectionIds: [...travel.viewedQuestionnaireSectionIds],
  };
}

export function fromApiTravel(travel: Travel): TravelRecord {
  return {
    id: travel.id,
    healthRecordId: travel.healthRecordId,
    travel: {
      journey: {
        legs: travel.journey.legs.map((leg) => ({
          countrycode: leg.countryCode,
          start_date: fromApiDate(leg.startDate),
          end_date: fromApiDate(leg.endDate),
          staying_conditions: fromApiStayingCondition(leg.stayingConditions),
        })),
        staying_conditions: fromApiStayingCondition(travel.journey.stayingConditions),
      },
      conditions: travel.conditions.map(({ id, value }) => ({
        id,
        value: value as TravelSnapshot["conditions"][number]["value"],
      })),
      viewedQuestionnaireSectionIds: [...travel.viewedQuestionnaireSectionIds],
    },
    createdAt: travel.createdAt.toISOString(),
    updatedAt: travel.updatedAt.toISOString(),
  };
}
